import { useEffect, useRef, useState } from "react";
import { motion, animate } from "framer-motion";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { LEAD_TYPES } from "../services/leadEmail";
import { submitLegacyLead } from "../services/legacyLeadSubmission";

function LazyVideo({ videoId }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="w-full overflow-hidden aspect-video rounded-2xl">
      {isVisible ? (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&showinfo=0`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      ) : (
        <div className="w-full h-full bg-black" />
      )}
    </div>
  );
}

function HomeCapri() {
  const navigate = useNavigate();

  function AnimatedNumber({ to }) {
    const [value, setValue] = useState(0);

    useEffect(() => {
      const controls = animate(0, to, {
        duration: 2,
        onUpdate(val) {
          setValue(val);
        },
      });
      return () => controls.stop();
    }, [to]);

    return <span>{Math.round(value)}</span>;
  }

  const marketingCampaign = async (data, slug) => {
    try {
      await submitLegacyLead({
        collectionName: slug,
        formData: data,
        firestoreData: {
          name: data.name ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
          dealership: data.dealership ?? "",
          text: "",
          model: "CapriCampaign2025",
        },
        leadPayload: {
          type: LEAD_TYPES.CAMPAIGN_OFFER,
          vehicle: {
            model: "Capri",
          },
          campaign: {
            slug,
          },
          title: "capri-campaign-2025",
          message: "Запитване за Capri Campaign 2025",
        },
      });

      navigate("/thank-you-testdrive");
    } catch (error) {
      console.error("Грешка при запис в Firebase:", error);
    }
  };

  function RenderContactForm() {
    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm();
    // Use the updated campaign slug
    const slug = "capri-campaign-2025";

    return (
      <form
        onSubmit={handleSubmit((data) => marketingCampaign(data, slug))}
        id="templateCampaignForm"
        className="px-8 py-16 lg:px-48"
        noValidate
      >
        <h2 className="text-xl tracking-wider text-center uppercase text-fordBlue">
          Поискай оферта
        </h2>
        <div className="flex flex-col max-w-6xl gap-6 mx-auto mt-8">
          {/* Име и Фамилия */}
          <div>
            <label htmlFor="name" className="text-gray-500">
              Име и фамилия
            </label>
            <input
              type="text"
              id="name"
              name="name"
              {...register("name", {
                required: {
                  value: true,
                  message: "Моля, въведете Вашето име и фамилия",
                },
              })}
            />
            <p className="text-red-500">{errors.name?.message}</p>
          </div>
          {/* Имейл */}
          <div>
            <label htmlFor="email" className="text-gray-500">
              Имейл
            </label>
            <input
              type="email"
              id="email"
              name="email"
              {...register("email", {
                required: {
                  value: true,
                  message: "Моля, въведете имейл-адрес",
                },
                pattern: {
                  value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                  message: "Невалиден имейл-адрес",
                },
              })}
            />
            <p className="text-red-500">{errors.email?.message}</p>
          </div>
          {/* Телефон */}
          <div>
            <label htmlFor="phone" className="text-gray-500">
              Телефон
            </label>
            <input
              type="text"
              id="phone"
              name="phone"
              {...register("phone", {
                required: {
                  value: true,
                  message: "Моля, въведете телефонен номер",
                },
                pattern: {
                  value:
                    /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                  message: "Невалиден телефонен номер",
                },
              })}
            />
            <p className="text-red-500">{errors.phone?.message}</p>
          </div>
          {/* Дилърство */}
          <div>
            <label htmlFor="dealership" className="text-gray-500">
              Предпочитано представителство
            </label>
            <select
              name="dealership"
              id="dealership"
              {...register("dealership", {
                required: {
                  value: true,
                  message: "Моля, изберете представителство",
                },
              })}
            >
              <option value="" disabled>
                Моля, изберете
              </option>
              <option value="liulin2@motopfohe.bg">София Люлин</option>
              <option value="mladost@motopfohe.bg">София Младост</option>
              <option value="blagoevgrad@motopfohe.bg">Благоевград</option>
              <option value="burgas@motopfohe.bg">Бургас</option>
              <option value="varna@motopfohe.bg">Варна</option>
              <option value="velikotarnovo@motopfohe.bg">Велико Търново</option>
              <option value="gabrovo@motopfohe.bg">Габрово</option>
              <option value="dobrich@motopfohe.bg">Добрич</option>
              <option value="montana@motopfohe.bg">Монтана</option>
              <option value="pleven@motopfohe.bg">Плевен</option>
              <option value="plovdiv@motopfohe.bg">Пловдив</option>
              <option value="russe@motopfohe.bg">Русе</option>
              <option value="starazagora@motopfohe.bg">Стара Загора</option>
              <option value="smolyan@motopfohe.bg">Смолян</option>
              <option value="g.draganov@autobox.bg">Хасково</option>
              <option value="forsh@motopfohe.bg">Шумен</option>
            </select>
            <p className="text-red-500">{errors.dealership?.message}</p>
          </div>
        </div>
        {/* Скрито поле за заглавие на кампанията */}
        <input type="hidden" name="title" value="capri-campaign-2025" />
        <div className="flex flex-col items-center mt-8">
          <button
            type="submit"
            className="px-16 py-2 mx-auto text-white border rounded-full bg-fordBlue"
          >
            Изпрати
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="w-full font-fordAntennaRegular">
      <section className="flex justify-center w-full">
        <picture>
          <source
            media="(min-width: 768px)"
            srcSet="/images/capri-campaign-2025/Capri-3840х1600px.jpg"
          />
          <img
            src="/images/capri-campaign-2025/Capri-768х950px.jpg"
            alt="Ford Capri Hero"
            className="object-cover w-full"
          />
        </picture>
      </section>
      {/* Get in touch buttons 1 */}
      <section className="w-full py-10 bg-gray-100 md:py-16">
        <div className="flex flex-col items-center justify-center gap-8 mx-auto max-w-7xl md:flex-row md:justify-around">
          <a
            href="#poiskayOferta"
            className="inline-block px-6 py-3 tracking-widest text-white transition rounded-full bg-fordBlue hover:bg-fordLight"
          >
            Заяви оферта
          </a>
          <a
            href="https://sale.ford.bg/?model=CAPRI"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 tracking-widest text-white transition rounded-full bg-fordBlue hover:bg-fordLight"
          >
            Виж налични автомобили
          </a>
        </div>
      </section>
      {/* Numbers */}
      <div className="py-16 mx-auto max-w-7xl lg:py-36 text-fordBlue">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 gap-8 text-center lg:text-left md:grid-cols-3">
          <div>
            <p className="mb-2 text-sm text-gray-700 uppercase">0-100 km/h</p>
            <p className="mt-4 text-6xl font-bold">
              <AnimatedNumber to={5.3} />{" "}
              <span className="text-lg font-normal">сек</span>
            </p>
          </div>
          <div>
            <p className="mb-2 text-sm text-gray-700 uppercase">
              Максимален пробег
            </p>
            <p className="mt-4 text-6xl font-bold">
              <AnimatedNumber to={627} />{" "}
              <span className="text-lg font-normal">км</span>
            </p>
          </div>
          <div>
            <p className="mb-2 text-sm text-gray-700 uppercase">
              Зареждане на бързозарядна станция
            </p>
            <p className="mt-4 text-6xl font-bold">
              <AnimatedNumber to={199} />{" "}
              <span className="text-lg font-normal">км</span>
            </p>
          </div>
        </div>
      </div>
      {/* Intro text */}
      <div className="mx-auto max-w-7xl text-fordBlue">
        <p className="px-8 text-3xl leading-tight">
          Новият Ford Capri<span className="text-xs align-super">®</span> вече е
          тук - с дух на спортен автомобил в практичен SUV. Изцяло електрическо
          купе с усъвършенствани технологии, емблематичен дизайн и възможност за
          шофиране през целия ден. Предлага се в две модификации.
        </p>
      </div>
      {/* Two Yellow Cars */}
      <div className="flex flex-col justify-between gap-8 px-12 mx-auto my-20 md:flex-row max-w-7xl text-fordBlue">
        <div className="text-fordBlue">
          <img
            src="https://www.fr.ford.be/content/dam/guxeu/rhd/central/cars/cx740l/reveal/nameplate/ford-cx740l-eu-style_model-3x2-1000x667_LHD.jpg"
            alt="Ford Capri Style"
          />
          <p className="mb-4 text-4xl font-bold">Ford Capri Style</p>
          <p>
            Почувствайте спортния лукс в Capri Style, който е част от
            стандартното оборудване. Включва частична кожена тапицерия Sensico
            (6), електрическа спортна седалка на водача с функция масаж,
            отопляеми седалки и спортен отопляем волан. Освен всичко това,
            автомобилът се предлага с батерия 52 kW AWD, осигуряваща динамично
            задвижване и сигурност на пътя.
          </p>
        </div>
        <div className="text-fordBlue">
          <img
            src="https://www.fr.ford.be/content/dam/guxeu/rhd/central/cars/cx740l/reveal/nameplate/ford-cx740l-eu-premium_model-3x2-1000x667.jpg"
            alt="Ford Capri Premium"
          />
          <p className="mb-4 text-4xl font-bold">Ford Capri Premium</p>
          <p>
            Насладете се на подобрени характеристики с Capri Premium,
            включително 20-инчови алуминиеви джанти, електрическа задна врата,
            фиксиран панорамен покрив*, матрични LED фарове и премиум
            аудиосистема B&O с 10 високоговорителя. Моделът се предлага с
            батерия 77 kW със задно задвижване, както и във вариант с 79 kW AWD,
            за да получите идеалния баланс между динамика, ефективност и
            контрол.
          </p>
        </div>
      </div>
      {/* Video 1 */}
      <div className="flex justify-center px-8 mx-auto overflow-hidden rounded-2xl max-w-7xl">
        <LazyVideo videoId="7V2H8PKKm50" />
      </div>

      {/* High-end features with image */}
      <div className="relative w-full mt-24 text-fordBlue">
        <img
          src="https://www.ford.fr/content/dam/guxeu/rhd/central/cars/cx740l/reveal/nameplate/ford-cx740l-eu-Banner_07_CX740L_INTERIOR-16x9-1920x1080-bb-interior-view-cx740l.jpg"
          alt="Ford Capri Interior"
          className="w-full"
        />
        <p className="absolute lg:text-6xl font-bold text-white left-[10%] bottom-[15%] drop-shadow-lg">
          Функции от най-висок клас
        </p>
      </div>
      {/* 3 high-end features */}
      <div className="px-4 bg-black">
        <div className="py-20 mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="text-white">
              <h3 className="mb-2 text-2xl font-bold">
                14.6" подвижен сензорен екран
              </h3>
              <p>
                Задайте оптималната позиция на централния сензорен екран SYNC
                Move, който се регулира според позицията ви на шофиране.
              </p>
            </div>
            <div className="text-white">
              <h3 className="mb-2 text-2xl font-bold">Спортни седалки</h3>
              <p>
                Отпуснете се в отопляемата шофьорска седалка с регулиране в 12
                посоки, лумбална опора и интегрирана облегалка за глава.
              </p>
            </div>
            <div className="text-white">
              <h3 className="mb-2 text-2xl font-bold">Амбиентно осветление</h3>
              <p>
                Създайте настроение - от спокойно до бунтарско - с
                персонализирано вътрешно осветление
              </p>
            </div>
          </div>
        </div>
        {/* Second video */}
        <div className="flex justify-center mx-auto overflow-hidden rounded-2xl max-w-7xl">
          <LazyVideo videoId="wsa_ac9uTlQ" />
        </div>
        {/* Text block with updated text */}
        <div className="mx-auto text-white py-36 max-w-7xl">
          <p className="text-3xl font-semibold text-center leading-xl">
            До 572 литра багажно пространство и електрическа врата на багажника
          </p>
        </div>
      </div>
      {/* Charging banner */}
      <div className="relative w-full">
        <picture>
          <source
            srcSet="https://www.ford.fr/content/dam/guxeu/rhd/central/cars/cx740l/reveal/nameplate/ford-cx740l-eu-CX740L_CHARGING_HERO_Talent_BANNER-16x9-1920x1080-side-view-of-blue-cx740l.jpg"
            media="(min-width: 1200px)"
          />
          <source
            srcSet="https://www.ford.fr/content/dam/guxeu/rhd/central/cars/cx740l/reveal/nameplate/ford-cx740l-eu-CX740L_CHARGING_HERO_Talent_BANNER-16x9-1920x1080-side-view-of-blue-cx740l.jpg"
            media="(min-width: 480px)"
          />
          <source
            srcSet="https://www.ford.fr/content/dam/guxeu/rhd/central/cars/cx740l/reveal/nameplate/ford-cx740l-eu-CX740L_CHARGING_HERO_Talent_BANNER-9x16-780x1385-side-view-of-blue-cx740l.jpg"
            media="(min-width: 0px)"
          />
          <img
            src="https://www.ford.fr/content/dam/guxeu/rhd/central/cars/cx740l/reveal/nameplate/ford-cx740l-eu-CX740L_CHARGING_HERO_Talent_BANNER-16x9-1920x1080-side-view-of-blue-cx740l.jpg"
            alt="Blue electric Ford Capri® connected to a public charging station."
            className="w-full"
          />
        </picture>
        <div className="absolute text-white left-[8%] bottom-[10%]">
          <p className="mb-2 font-bold leading-tight text-3 md:text-5xl">
            ЗАРЕЖДАНЕ
          </p>
          <p className="text-xl">
            С възможност за бързо зареждане до 80% само за 26 мин
          </p>
        </div>
      </div>
      {/* 5 star security */}
      <div className="flex flex-col justify-between mx-auto my-20 md:gap-8 md:flex-row max-w-7xl text-fordBlue">
        <div className="px-4 overflow-hidden md:px-24 rounded-xl">
          <img
            src="https://www.fr.ford.be/content/dam/guxeu/rhd/central/cars/cx740l/reveal/nameplate/ford-cx740l-eu-Small-card-15_CX740L_SIDE_PROFILE-1x1-1000x1000_side-profile.jpg"
            alt="Ford Capri 5 star safety"
            className="rounded-xl"
          />
        </div>
        <div className="px-4 py-24 text-fordBlue">
          <p className="mb-4 text-4xl font-bold">5-ЗВЕЗДНА СИГУРНОСТ</p>
          <p>
            Ford Capri<span className="text-xs align-super">®</span> е удостоен
            с най-високия рейтинг за безопасност от Euro NCAP и е оценен с 5
            звезди, гарантирайки спокойствие на всяко пътуване.
          </p>
        </div>
      </div>

      <motion.section
        id="poiskayOferta"
        className="w-full py-8 mx-auto bg-gray-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, delay: 1.3 }}
      >
        <RenderContactForm />
      </motion.section>

      <section className="py-16 text-center">
        <a
          href="https://ford.bg/cars/capri"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block px-8 py-4 text-white transition rounded-full bg-fordBlue hover:bg-fordLight"
        >
          Виж повече за модела
        </a>
      </section>
    </div>
  );
}

export default HomeCapri;
